import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';

import { AuthService } from '../../core/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { UserStore } from '../../core/services/user-store.service';

function passwordsIguales(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmacion = group.get('confirmacion')?.value;
  if (!password || !confirmacion || password === confirmacion) {
    return null;
  }
  return { mismatch: true };
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, Card, InputText, Dialog, Button, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly messages = inject(MessageService);
  private readonly userStore = inject(UserStore);
  readonly theme = inject(ThemeService);

  readonly loginForm = this.fb.nonNullable.group({
    mailAccess: ['', [Validators.required, Validators.email]],
    password: [''],
  });

  readonly passwordForm = this.fb.nonNullable.group(
    {
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmacion: ['', [Validators.required]],
    },
    { validators: passwordsIguales },
  );

  loading = false;
  readonly sesionActiva = this.auth.isAuthenticated();
  readonly definiendoPassword = signal(false);
  readonly guardandoPassword = signal(false);
  private setupToken = '';

  onSubmit(): void {
    if (this.loginForm.invalid || this.definiendoPassword()) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.auth.login(this.loginForm.getRawValue()).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.data.debeDefinirPassword && response.data.setupToken) {
          this.setupToken = response.data.setupToken;
          this.passwordForm.reset();
          this.definiendoPassword.set(true);
          return;
        }
        this.irAlInicio();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messages.add({
          severity: 'error',
          summary: 'No pudimos iniciar sesión',
          detail: this.resolveErrorDetail(err),
          life: 5000,
        });
      },
    });
  }

  guardarPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const password = this.passwordForm.controls.password.value;
    const mailAccess = this.loginForm.controls.mailAccess.value;
    this.guardandoPassword.set(true);
    this.auth.definirPassword(this.setupToken, password).subscribe({
      next: () => {
        this.auth.login({ mailAccess, password }).subscribe({
          next: () => {
            this.guardandoPassword.set(false);
            this.definiendoPassword.set(false);
            this.setupToken = '';
            this.irAlInicio();
          },
          error: (err: HttpErrorResponse) => {
            this.guardandoPassword.set(false);
            this.definiendoPassword.set(false);
            this.setupToken = '';
            this.loginForm.controls.password.setValue(password);
            this.messages.add({
              severity: 'error',
              summary: 'Contraseña guardada',
              detail: this.resolveErrorDetail(err),
              life: 5000,
            });
          },
        });
      },
      error: (err: HttpErrorResponse) => {
        this.guardandoPassword.set(false);
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message ?? 'No se pudo guardar la contraseña.',
          life: 5000,
        });
      },
    });
  }

  cancelarPassword(): void {
    this.definiendoPassword.set(false);
    this.passwordForm.reset();
    this.setupToken = '';
    this.auth.logout();
  }

  entrarConSesion(): void {
    void this.router.navigateByUrl(this.userStore.rutaInicial());
  }

  private irAlInicio(): void {
    this.entrarConSesion();
  }

  private resolveErrorDetail(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'No se pudo contactar al servidor.';
    }
    if (err.status === 401 || err.status === 422) {
      return 'Correo o contraseña incorrectos.';
    }
    return err.error?.message ?? 'Intentá de nuevo en unos minutos.';
  }
}
